import type { ReactNode } from 'react';

/**
 * CHAT-DUET icons: 24-unit stroke icons that take the colour of their text.
 *
 * The plus, mic, up-arrow, waveform, clock, chat bubble, task list and dots are OpenDuet's paths
 * (github.com/CopilotKit/OpenDuet, openbot/app/src/components/duet/icons.tsx) - MIT License, Copyright (c) 2026
 * CopilotKit. The speaker, paperclip, refresh, conversation and briefcase are drawn here in the same style.
 */
function Icon({ className = 'h-5 w-5', stroke = 2, children }: { className?: string; stroke?: number; children: ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

type P = { className?: string };

export const PlusIcon = ({ className }: P) => <Icon className={className}><path d="M5 12h14M12 5v14" /></Icon>;
export const MicIcon = ({ className }: P) => (
  <Icon className={className}>
    <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
  </Icon>
);
export const ArrowUpIcon = ({ className }: P) => <Icon className={className} stroke={2.4}><path d="m5 12 7-7 7 7M12 19V5" /></Icon>;
export const WaveIcon = ({ className }: P) => <Icon className={className}><path d="M3 10v4M7.5 7v10M12 4v16M16.5 8v8M21 10.5v3" /></Icon>;
export const ClockIcon = ({ className }: P) => (
  <Icon className={className}><circle cx="12" cy="12" r="9.5" /><path d="M12 7v5l3.2 2" /></Icon>
);
export const SpeakerIcon = ({ className }: P) => (
  <Icon className={className}><path d="M11 5 6 9H3v6h3l5 4z" /><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /></Icon>
);
export const ClipIcon = ({ className }: P) => (
  <Icon className={className}>
    <path d="M15.2 7 8.6 13.6a2 2 0 1 0 2.8 2.8l6.4-6.6a4 4 0 0 0-5.6-5.6l-6.4 6.6a6 6 0 1 0 8.5 8.5L20.5 13" />
  </Icon>
);
export const RefreshIcon = ({ className }: P) => (
  <Icon className={className}>
    <path d="M20 11a8 8 0 0 0-14.7-4.3L4 8.5" /><path d="M4 4v4.5h4.5" />
    <path d="M4 13a8 8 0 0 0 14.7 4.3l1.3-1.8" /><path d="M20 20v-4.5h-4.5" />
  </Icon>
);
export const ChatIcon = ({ className }: P) => <Icon className={className} stroke={1.9}><path d="M7.9 20A9 9 0 1 0 4 16.1L2.5 21.5z" /></Icon>;
export const TasksIcon = ({ className }: P) => (
  <Icon className={className} stroke={1.9}>
    <rect x="3" y="4" width="6" height="6" rx="1.5" /><path d="m3 17 2 2 4-4M13 5h8M13 12h8M13 19h8" />
  </Icon>
);
export const ConvoIcon = ({ className }: P) => (
  <Icon className={className} stroke={1.9}>
    <path d="M20.5 15.5a2 2 0 0 1-2 2H8l-4.5 3.5V5.5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" /><path d="M8 9h8M8 12.5h5" />
  </Icon>
);
export const DealsIcon = ({ className }: P) => (
  <Icon className={className} stroke={1.9}>
    <rect x="3" y="7" width="18" height="13" rx="2.5" /><path d="M8.5 7V5.5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2V7M3 12.5h18" />
  </Icon>
);
export const MoreIcon = ({ className }: P) => (
  <Icon className={className} stroke={2.6}>
    <circle cx="5.5" cy="12" r="0.6" /><circle cx="12" cy="12" r="0.6" /><circle cx="18.5" cy="12" r="0.6" />
  </Icon>
);
